import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Globe } from "lucide-react";
import { getRoute, getRouteMatchFromPath } from "../utils/routes";

const LANGUAGES = [
  { code: "tr", label: "TR", flag: "🇹🇷" },
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "cs", label: "CS", flag: "🇨🇿" },
];

const LanguageSwitcher = ({ isScrolled = false }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const resolvedLang = i18n.resolvedLanguage || i18n.language;
  const currentLang =
    LANGUAGES.find((lang) => resolvedLang?.startsWith(lang.code))?.code || "tr";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectLanguage = async (newLang) => {
    setIsOpen(false);
    if (newLang === currentLang) return;
    await i18n.changeLanguage(newLang);

    const homePaths = ["/", "/en", "/cs"];
    if (homePaths.includes(location.pathname)) {
      navigate(newLang === "tr" ? "/" : `/${newLang}`);
      return;
    }

    const routeMatch = getRouteMatchFromPath(location.pathname);
    if (routeMatch?.key) {
      const newPath = getRoute(routeMatch.key, newLang, routeMatch.params);
      navigate(newPath);
    }
  };

  const current = LANGUAGES.find((lang) => lang.code === currentLang);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen((open) => !open)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
          isScrolled
            ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
            : "bg-white/10 text-white hover:bg-white/20"
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <Globe className="w-4 h-4" />
        <span>{current.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-36 rounded-xl bg-white shadow-lg border border-gray-100 py-1.5 z-50"
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              role="option"
              aria-selected={lang.code === currentLang}
              onClick={() => selectLanguage(lang.code)}
              className={`w-full flex items-center gap-2 px-3.5 py-2 text-sm text-left transition-colors ${
                lang.code === currentLang
                  ? "text-accent-teal font-semibold"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
